import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Plus, Tent } from 'lucide-react';

/**
 * Summer Adventure Hero Card Component
 * Matches the design of the Discover page hero card
 */
const SummerAdventureHero = ({ onDiscover, onAddCamp, className = '' }) => {
  const navigate = useNavigate();

  const handleDiscoverClick = () => {
    // Always navigate to Discovery page
    navigate('/Discovery');
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hero Background with Gradient - Matching Discover page */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-600 via-brand-700 to-teal-600 rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-600/50 to-teal-600/50"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-4 right-4 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-4 left-4 w-24 h-24 bg-white/5 rounded-full blur-2xl"></div>
      </div>

      {/* Hero Content - White Card */}
      <div className="relative px-6 py-8 md:px-8 md:py-10">
        <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
          <CardContent className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
                Your Summer Adventure Awaits! ✨
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
                Discover the perfect camps for your child or add ones you've already found. 
                Let's make this summer unforgettable!
              </p>
              
              {/* No Saved Camps Section */}
              <div className="flex flex-col items-center gap-3 mt-6">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Tent className="w-8 h-8 text-gray-400" />
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-900 mb-1">
                    No Saved Camps Yet
                  </p>
                  <p className="text-base text-gray-600">
                    Start building your summer camp collection!
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {/* Primary CTA - Discover */}
              <Button
                type="button"
                onClick={handleDiscoverClick}
                className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <span className="flex items-center justify-center gap-2">
                  <Search className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  Discover Camps
                </span>
              </Button>

              {/* Secondary CTA - Add Manually */}
              <Button
                onClick={onAddCamp}
                variant="outline"
                className="w-full sm:w-auto px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200"
              >
                <span className="flex items-center justify-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add Camp Manually
                </span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SummerAdventureHero;

